import { tokenService } from './tokenService';

const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface SseEvent {
    text?: string;
    done?: boolean;
    content?: string;
    messageId?: number;
    error?: string;
}

export interface StreamOptions {
    onEvent: (event: SseEvent) => void;
    signal?: AbortSignal;
}

/**
 * Tranche D (#489): POST + read an `text/event-stream` response line by line.
 * `fetch` instead of axios because axios buffers the body; the token comes
 * from the same place the axios interceptor uses. Throws on HTTP errors with
 * the API's `message`/`error` when available.
 */
export async function streamPost(path: string, body: unknown, { onEvent, signal }: StreamOptions): Promise<void> {
    const token = tokenService.getToken();
    const response = await fetch(`${apiBaseUrl}/api${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
        signal,
    });
    if (!response.ok) {
        let message = `HTTP ${response.status}`;
        try {
            const data = await response.json();
            message = data?.message ?? data?.error ?? message;
        } catch {
            /* non-JSON error body */
        }
        const error = new Error(message) as Error & { status?: number };
        error.status = response.status;
        throw error;
    }
    if (!response.body) {
        throw new Error('Kein Antwort-Stream.');
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let index = buffer.indexOf('\n');
        while (index !== -1) {
            const line = buffer.slice(0, index).trim();
            buffer = buffer.slice(index + 1);
            if (line.startsWith('data:')) {
                const json = line.slice(5).trim();
                if (json && json !== '[DONE]') {
                    try {
                        onEvent(JSON.parse(json) as SseEvent);
                    } catch {
                        /* ignore malformed event */
                    }
                }
            }
            index = buffer.indexOf('\n');
        }
    }
}
